<script setup lang="ts">
import { ref, onMounted } from "vue";

const props = defineProps<{
  navigationId: string;
}>();

const { apiClient } = useShopwareContext();
const { buildDynamicBreadcrumbs, pushBreadcrumb } = useBreadcrumbs();

// Server-side data fetching
const { data: recipe, pending, error } = await useAsyncData(
  `recipe-${props.navigationId}`,
  async () => {
    try {
      const response = await apiClient.invoke(
        "getRecipes post /recipes/recipe-detail/{recipeId}",
        {
          pathParams: { recipeId: props.navigationId },
        }
      );
      return response?.data.recipe || {};
    } catch (err) {
      console.error("Error loading recipe:", err);
      throw err;
    }
  }
);

// Add schema markup and SEO after data is loaded
if (recipe.value) {
  const recipeSchema = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    "name": recipe.value?.title,
    "description": recipe.value?.translated?.description || recipe.value?.title,
    "image": recipe.value?.cover?.media?.url,
    "author": recipe.value?.author || "Kisag",
    "datePublished": recipe.value?.createdAt,
    "prepTime": recipe.value?.preparationTime ? `PT${recipe.value.preparationTime}M` : undefined,
    "cookTime": recipe.value?.cookingTime ? `PT${recipe.value.cookingTime}M` : undefined,
    "recipeYield": recipe.value?.portions ? `${recipe.value.portions} servings` : undefined,
    "recipeIngredient": recipe.value?.ingredientLists?.flatMap((list: any) => 
      list.ingredients?.map((ingredient: any) => 
        `${ingredient.quantity} ${ingredient.unit} ${ingredient.title}`
      ) || []
    ) || [],
    "interactionStatistic": {
      "@type": "InteractionCounter",
      "interactionType": "https://schema.org/Comment",
      "userInteractionCount": 0
    },
    "recipeInstructions": recipe.value?.preparations?.flatMap((prep: any) => 
      prep.steps?.map((step: any) => step.description)
    ).join(" ") || "",
    "nutrition": {
      "@type": "NutritionInformation",
      "calories": recipe.value?.kiloCalories ?? undefined,
      "fatContent": recipe.value?.fat ?? undefined,
      "kiloJoules": recipe.value?.kiloJoules ?? undefined,
      "proteins": recipe.value?.proteins ?? undefined,
      "carbohydrates": recipe.value?.carbohydrates ?? undefined,
      "saturatedFat": recipe.value?.saturatedFat ?? undefined,
      "unsaturatedFat": recipe.value?.unsaturatedFat ?? undefined,
      "transFat": recipe.value?.transFat ?? undefined,
      "fiber": recipe.value?.fiber ?? undefined,
      "sugar": recipe.value?.sugar ?? undefined,
      "salt": recipe.value?.salt ?? undefined,
      "cholesterol": recipe.value?.cholesterol ?? undefined,
    }
  };

  // Set SEO meta tags
  useSeoMeta({
    title: recipe.value?.translated?.metaTitle || recipe.value?.title,
    description: recipe.value?.translated?.description || recipe.value?.title,
    ogTitle: recipe.value?.translated?.metaTitle || recipe.value?.title,
    ogDescription: recipe.value?.translated?.description || recipe.value?.title,
    ogImage: recipe.value?.cover?.media?.url,
    twitterCard: 'summary_large_image',
  });

  // Add schema markup
  useHead({
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(recipeSchema)
      }
    ]
  });

  // Set up breadcrumbs
  if (recipe.value?.categories?.[0]?.id) {
    try {
      const breadcrumbs = await apiClient.invoke(
        "readBreadcrumb get /breadcrumb/{id}",
        {
          pathParams: {
            id: recipe.value.categories[0].id,
          },
        }
      );

      buildDynamicBreadcrumbs(breadcrumbs.data);

      pushBreadcrumb({
        name: recipe.value.title ?? "",
        path: "",
      });
    } catch (error) {
      console.error("Error fetching breadcrumbs:", error);
    }
  }
}
</script>

<template>
  <section>
    <LayoutBreadcrumbs />
    <LoadingOverlay :show="pending" />
    <CommonRecipeDetail v-if="recipe" :recipe-data="recipe" />
  </section>
</template>
